import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import { Table } from "@/app/entities/TableCredit";
import { useDebounce } from "@/app/hooks/useDebounce";
import { useFGTSMutations } from "@/app/hooks/useFGTSMutations";
import { useSelfHiringMutation } from "@/app/hooks/useSelfHiringMutation";
import { PartnerType } from "@/app/utils/partners.enum";
import { parse } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useParams } from "react-router-dom";

export const useInstallments = (methods: UseFormReturn<any>) => {
  const { linkName } = useParams();
  const { simulateFGTSMutation } = useFGTSMutations();
  const { setFgtsData, panData, setPanData, cltData, setCltData, type } =
    useSelfHiringContext();
  const { generateOffersPanMutation, simulateCltMutation } =
    useSelfHiringMutation();

  const [cltFinancedValue, setCltFinancedValue] = useState<number | undefined>(
    undefined,
  );
  const [cltMessage, setCltMessage] = useState<string | undefined>(undefined);
  const [cltPrazo, setCltPrazo] = useState<number | undefined>(undefined);
  const debouncedCltValue = useDebounce(1500, cltFinancedValue?.toString());

  const simulateClt = async (params?: {
    valorFinanciado?: number;
    quantidadeParcelas?: number;
  }) => {
    const formData = methods.getValues() as any;
    const cpf = formData?.cpf?.replace(/\D/g, "");
    if (!cpf || !formData?.birthDate || !linkName) return;

    try {
      const response = await simulateCltMutation.mutateAsync({
        cpf,
        params: {
          linkName,
          birthDate: parse(
            formData.birthDate,
            "dd/MM/yyyy",
            new Date(),
          ).toISOString(),
          valorFinanciado: params?.valorFinanciado,
          quantidadeParcelas: params?.quantidadeParcelas,
        },
      });

      if (!response.available || !response.tables?.length) {
        setCltMessage(response.message || "Nenhuma oferta encontrada");
        setCltData((prev) =>
          prev ? { ...prev, tables: [], selectedIndex: 0 } : prev,
        );
        return;
      }

      setCltMessage(undefined);
      setCltData((prev) =>
        prev
          ? {
              ...prev,
              matricula: response.matricula ?? prev.matricula,
              cnpjEmpresa: response.cnpjEmpresa ?? prev.cnpjEmpresa,
              prazos: response.prazos ?? prev.prazos,
              tables: response.tables,
              selectedIndex: 0,
            }
          : prev,
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (type !== PartnerType.CLT) return;
    if (debouncedCltValue === undefined || debouncedCltValue === "") return;
    if (Number(debouncedCltValue) <= 0) return;
    void simulateClt({
      valorFinanciado: Number(debouncedCltValue),
      quantidadeParcelas: cltPrazo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCltValue]);

  const handleCltRefresh = () => {
    setCltFinancedValue(undefined);
    setCltPrazo(undefined);
    setCltMessage(undefined);
    void simulateClt();
  };

  const handleSelectCltPrazo = (quantidadeParcelas: number) => {
    if (!Number.isFinite(quantidadeParcelas)) return;
    setCltPrazo(quantidadeParcelas);
    void simulateClt({
      valorFinanciado: cltFinancedValue,
      quantidadeParcelas,
    });
  };
  const selectedCondition = useMemo(
    () =>
      panData?.state?.offers?.conditions?.find((cond) => cond.sucesso) ?? null,
    [panData?.state?.offers?.conditions],
  );

  const handleMakeProposal = () => {
    const { parcelas, ...operacoesCreditoSemParcelas } =
      selectedCondition ?? {};
    setPanData({
      ...panData,
      proposal: {
        operacoes_credito: operacoesCreditoSemParcelas,
        customerId: panData?.state.customerId,
      },
    });
  };

  const handleChangeInstallment = async (selected?: number) => {
    if (type === PartnerType.CLT) {
      if (selected === undefined || !cltData?.tables?.length) return;
      if (!cltData.tables[selected]) return;
      setCltData({ ...cltData, selectedIndex: selected });
      return;
    }

    const prazo = selected;

    if (type === PartnerType.PAN) {
      if (!panData?.state) return;

      const { customerId, preTransactionId, selectedValue } = panData.state;
      if (!customerId || !preTransactionId) return;
      if (!prazo) return;

      const formData = methods.getValues() as any;
      const cpf = formData?.cpf?.replace(/\D/g, "");
      if (!cpf || !linkName) return;

      const response = await generateOffersPanMutation.mutateAsync({
        linkName,
        payload: {
          cpf,
          prazo,
          valor: selectedValue,
          proposalId: preTransactionId,
        },
      });

      setPanData((prev: any) => ({
        ...prev,
        state: {
          ...panData.state,
          offers: response,
          selectedPrazo: prazo,
        },
      }));

      return;
    }

    // FGTS
    const formData = methods.getValues() as any;
    if (!formData?.cpf || !formData?.birthDate) return;
    try {
      const response = await simulateFGTSMutation.mutateAsync({
        cpf: formData.cpf,
        birthDate: parse(
          formData.birthDate,
          "dd/MM/yyyy",
          new Date(),
        ).toISOString(),
        linkName: linkName || "",
        prazo,
      });

      const validTable = response.tables.find(
        (table) => table.success === true && table.ValorLiquidoCliente > 0,
      );

      if (validTable) {
        setFgtsData((prev: any) => ({
          ...prev,
          table: validTable as unknown as Table,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  return {
    handleMakeProposal,
    handleChangeInstallment,
    cltFinancedValue,
    setCltFinancedValue,
    handleCltRefresh,
    cltMessage,
    cltPrazo,
    handleSelectCltPrazo,
    reloadTable:
      type === PartnerType.PAN
        ? generateOffersPanMutation.isPending
        : type === PartnerType.CLT
          ? simulateCltMutation.isPending
          : simulateFGTSMutation.isPending,
  };
};
